import React, { useEffect, useRef, useState } from "react";
import { Phone, Truck, User } from "lucide-react";

const DispatchOrderModel = ({
  order,
  onClose,
  updateOrderStatus,
  sendReceipt,
  getCatalogOrder,
  assignDeliveryPartner,
  notifyDriver,
  updateScheduleOrder,
  getDeliveryPersons,
  userID,
  deliveryPersons = [],
}) => {
  const [selectedService, setSelectedService] = useState("packky");
  const [selectedRider, setSelectedRider] = useState(null);
  const [assignedPartner, setAssignedPartner] = useState(null);
  const [foodReadyLoading, setFoodReadyLoading] = useState(false);
  const [foodReadyClicked, setFoodReadyClicked] = useState(false);

  const scheduleUpdateInProgressRef = useRef(false);
  const scheduleUpdateTimeoutRef = useRef(null);

  const [foodReadyTimer, setFoodReadyTimer] = useState({
    isAllowed: false,
    remainingSeconds: 0,
    remainingText: "00:00",
  });

  const orderStatus = String(order?.status || "")
    .toLowerCase()
    .trim();

  const deliveryStatus = String(order?.delivery_partner_status || "")
    .toLowerCase()
    .trim();

  const isDispatched = deliveryStatus === "food is ready";

  const isUnassigned =
    deliveryStatus === "unassign" ||
    deliveryStatus === "unassigned" ||
    deliveryStatus === "" ||
    deliveryStatus === "null";

  const isAcknowledged = deliveryStatus === "acknowledged";

  const isFoodReady = deliveryStatus === "food is ready";

  const isTimerCompleted = foodReadyTimer.isAllowed;

  const foodReadyAlreadyDone = foodReadyClicked || isFoodReady;

  const showFoodReady =
    isTimerCompleted && isAcknowledged && !foodReadyAlreadyDone;

  const showWaitingForPartner =
    isTimerCompleted && !isAcknowledged && !isFoodReady && !foodReadyClicked;

  const showDriverDetailsAfterFoodReady = foodReadyAlreadyDone;

  const isProcessing =
    deliveryStatus === "food is ready" || deliveryStatus === "assigned";

  const shouldLockPackky =
    orderStatus === "dispatched" ||
    orderStatus === "food is ready" ||
    orderStatus === "assigned" ||
    orderStatus === "acknowledged" ||
    deliveryStatus === "food is ready" ||
    deliveryStatus === "acknowledged";

  const hasPartner =
    order?.delivery_person_name || order?.delivery_person_number;

  const formatCountdownTime = (seconds = 0) => {
    const safeSeconds = Number.isFinite(seconds) ? seconds : 0;

    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = safeSeconds % 60;

    return `${String(minutes).padStart(2, "0")}:${String(
      remainingSeconds,
    ).padStart(2, "0")}`;
  };

  const parseOrderDateTime = (orderDate) => {
    if (!orderDate) return null;

    // Example: 29/04/2026 12:30:00 PM
    const [datePart, timePart, period] = String(orderDate).split(" ");

    if (!datePart || !timePart) return null;

    const [day, month, year] = datePart.split("/").map(Number);
    let [hours, minutes, seconds] = timePart.split(":").map(Number);

    if (
      !day ||
      !month ||
      !year ||
      Number.isNaN(hours) ||
      Number.isNaN(minutes)
    ) {
      return null;
    }

    if (period?.toUpperCase() === "PM" && hours !== 12) hours += 12;
    if (period?.toUpperCase() === "AM" && hours === 12) hours = 0;

    return new Date(year, month - 1, day, hours, minutes, seconds || 0);
  };

  // schedule case
  const getScheduleDateTime = (order) => {
    return (
      order?.scheduled_date ||
      order?.schedule_date ||
      order?.scheduleDate ||
      order?.scheduledDate ||
      order?.schedule_time ||
      order?.scheduled_time ||
      ""
    );
  };

  const parseScheduleDateTime = (scheduledDate) => {
    if (!scheduledDate) return null;

    // API format: 2026-06-06 10:00:00
    const value = String(scheduledDate).trim();
    if (!value) return null;

    const [datePart, timePart] = value.split(" ");
    if (!datePart || !timePart) return null;

    const [year, month, day] = datePart.split("-").map(Number);
    const [hours, minutes, seconds] = timePart.split(":").map(Number);

    if (
      !year ||
      !month ||
      !day ||
      Number.isNaN(hours) ||
      Number.isNaN(minutes)
    ) {
      return null;
    }

    return new Date(year, month - 1, day, hours, minutes, seconds || 0);
  };

  const parseDeliveryTimeToMilliseconds = (deliveryTime) => {
    const value = String(deliveryTime ?? "").trim();

    if (!value) return 0;

    if (value.includes(".")) {
      const [minutePart = "0", secondPart = "0"] = value.split(".");

      const minutes = Number(minutePart) || 0;

      let seconds = Number(secondPart) || 0;

      if (secondPart.length === 1) {
        seconds *= 10;
      }

      return minutes * 60 * 1000 + seconds * 1000;
    }

    const minutes = Number(value);

    if (!Number.isFinite(minutes)) {
      return 0;
    }

    return minutes * 60 * 1000;
  };

  const getScheduleActiveFromTime = (order) => {
    const scheduledDate = getScheduleDateTime(order);

    if (!scheduledDate) {
      return null;
    }

    const scheduleTime = parseScheduleDateTime(scheduledDate);

    if (!scheduleTime) {
      return null;
    }

    const deliveryTimeMs = parseDeliveryTimeToMilliseconds(
      order?.delivery_time,
    );

    const extraTenMinutesMs = 10 * 60 * 1000;

    const totalBuffer = deliveryTimeMs + extraTenMinutesMs;

    return scheduleTime.getTime() - totalBuffer;
  };

  const getOrderPreparationMinutes = () => {
    try {
      const userData = JSON.parse(sessionStorage.getItem("user") || "{}");
      const minutes = Number(userData?.order_time);

      return Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
    } catch {
      return 0;
    }
  };

  const getScheduleUpdateLockKey = () => {
    return `schedule_update_called_${order?.orderid}`;
  };

  useEffect(() => {
    if (!order?.order_date || isFoodReady) return;

    const orderTimeMinutes = getOrderPreparationMinutes();
    const orderReceivedDate = parseOrderDateTime(order.order_date);
    const scheduleActiveFromTime = getScheduleActiveFromTime(order);

    if (!orderTimeMinutes || !orderReceivedDate) {
      const fallbackAllowedTime = scheduleActiveFromTime || Date.now();

      const updateTimer = () => {
        const remainingSeconds = Math.max(
          0,
          Math.ceil((fallbackAllowedTime - Date.now()) / 1000),
        );

        setFoodReadyTimer({
          isAllowed: remainingSeconds <= 0,
          remainingSeconds,
          remainingText: formatCountdownTime(remainingSeconds),
        });
      };

      updateTimer();

      const interval = setInterval(updateTimer, 1000);

      return () => clearInterval(interval);
    }

    const preparationAllowedTime =
      orderReceivedDate.getTime() + orderTimeMinutes * 60 * 1000;

    const allowedTime = scheduleActiveFromTime
      ? Math.max(preparationAllowedTime, scheduleActiveFromTime)
      : preparationAllowedTime;

    const updateTimer = () => {
      const remainingSeconds = Math.max(
        0,
        Math.ceil((allowedTime - Date.now()) / 1000),
      );

      setFoodReadyTimer({
        isAllowed: remainingSeconds <= 0,
        remainingSeconds,
        remainingText: formatCountdownTime(remainingSeconds),
      });
    };

    updateTimer();

    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [
    order?.order_date,
    order?.scheduled_date,
    order?.schedule_date,
    order?.scheduleDate,
    order?.scheduledDate,
    order?.schedule_time,
    order?.scheduled_time,
    isFoodReady,
  ]);

useEffect(() => {
  if (!order?.orderid) return;
  if (!updateScheduleOrder) return;
  if (!userID) return;

  const orderId = String(order.orderid);
  const lockKey = `schedule_update_called_${orderId}`;

  const scheduledDate = getScheduleDateTime(order);
  if (!scheduledDate) return;

  const scheduleActiveFromTime = getScheduleActiveFromTime(order);
  if (!scheduleActiveFromTime) return;

  const callScheduleUpdateApiOnce = async () => {
    const now = Date.now();

    // 10 min before schedule time se pehle API call nahi hogi
    if (now < scheduleActiveFromTime) return;

    // same orderid ke liye already API hit ho chuki hai
    if (localStorage.getItem(lockKey) === "true") {
      console.log("updateScheduleOrder skipped, already called:", orderId);
      return;
    }

    // same modal open me duplicate call stop
    if (scheduleUpdateInProgressRef.current) return;

    scheduleUpdateInProgressRef.current = true;
    
    localStorage.setItem(lockKey, "true");

    try {
      console.log("Calling updateScheduleOrder once:", {
        userId: userID,
        orderId1: orderId,
      });

      const res = await updateScheduleOrder({
        userId: userID,
        orderId1: orderId,
      });

      console.log("updateScheduleOrder response:", res);

      // response success ho to refresh karo
      if (res?.responseCode === "00") {
        await getCatalogOrder?.();
        window.dispatchEvent(new Event("orderUpdated"));
      } else {
        console.error(
          "updateScheduleOrder failed but locked for this order:",
          res?.responseMessage || res
        );
      }
    } catch (error) {
      console.error("updateScheduleOrder error but locked for this order:", error);
    } finally {
      scheduleUpdateInProgressRef.current = false;
    }
  };

  const delay = Math.max(0, scheduleActiveFromTime - Date.now());

  scheduleUpdateTimeoutRef.current = setTimeout(() => {
    callScheduleUpdateApiOnce();
  }, delay);

  return () => {
    if (scheduleUpdateTimeoutRef.current) {
      clearTimeout(scheduleUpdateTimeoutRef.current);
    }
  };
}, [order?.orderid, userID]);

  //  STEP 2: useEffect (AFTER logic)
  useEffect(() => {
    if (shouldLockPackky) {
      setSelectedService("packky");
    } else {
      setSelectedService((prev) => prev || "packky");
    }
  }, [shouldLockPackky]);

  useEffect(() => {
    if (isDispatched) {
      setSelectedService("packky");
    } else {
      setSelectedService((prev) => prev || "packky");
    }
  }, [isDispatched]);

  useEffect(() => {
    if (selectedService === "restaurant") {
      getDeliveryPersons();
    }
  }, [selectedService]);

  useEffect(() => {
    setSelectedRider(null);
    setAssignedPartner(null);

    if (order?.orderid) {
      const lockKey = `food_ready_clicked_${order.orderid}`;
      setFoodReadyClicked(sessionStorage.getItem(lockKey) === "true");
    }
  }, [order]);

  if (!order) return null;

  const addressObj = JSON.parse(order?.address_json || "{}")?.values || {};

  const fullAddress = [
    addressObj.address,
    addressObj.landmark_area,
    addressObj.city,
    addressObj.state,
    addressObj.in_pin_code,
  ]
    .filter(Boolean)
    .join(", ");

  if (order.orderType !== "home_delivery") {
    return (
      <div className="fixed inset-0 z-[9999] bg-black/40 flex justify-center items-center">
        <div
          className="bg-white rounded-lg w-[400px] shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          {/* HEADER */}
          <div className="flex justify-between items-center px-4 py-3 border-b">
            <h2 className="text-lg font-semibold capitalize">
              {order.orderType}
            </h2>
            <button onClick={onClose}>✕</button>
          </div>

          {/* BODY */}
          <div className="p-4">
            <p className="text-sm text-gray-600">
              {order.orderType === "takeaway"
                ? "Please prepare the order for pickup."
                : order.orderType === "dining"
                  ? "Order will be served in restaurant."
                  : "Are you sure you want to dispatch this order?"}
            </p>
          </div>

          {/* FOOTER */}
          <div className="flex justify-end gap-2 px-4 py-3 border-t">
            <button onClick={onClose} className="px-4 py-1.5 border rounded">
              Cancel
            </button>

            <button
              onClick={async () => {
                if (order.orderType === "takeaway") {
                  await updateOrderStatus(order.orderid, "ready_for_takeaway");
                } else if (order.orderType === "dining") {
                  await updateOrderStatus(order.orderid, "ready_for_dinning");
                } else {
                  await updateOrderStatus(order.orderid, "dispatched");
                }

                onClose();
              }}
              className="px-4 py-1.5 bg-blue-600 text-white rounded"
            >
              {order.orderType === "takeaway"
                ? "Ready For Takeaway"
                : order.orderType === "dining"
                  ? "Ready For Dining"
                  : "Confirm Dispatch"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>

    <div className="fixed inset-0 z-[9999] bg-black/40 flex justify-center items-center">
      <div className="bg-white rounded-lg w-[700px] shadow-xl">
        {/* HEADER */}
        <div className="flex justify-between items-center px-5 py-3 border-b">
          <h2 className="text-lg font-semibold">Home Delivery</h2>
          <button onClick={onClose}>✕</button>
        </div>

        {/* ADDRESS */}
        <div className="px-5 py-3">
          <p className="text-sm">
            <span className="font-semibold text-green-700">
              Delivery Address :
            </span>{" "}
            {fullAddress}
          </p>
        </div>

        <hr />

        {/* SERVICE SELECT */}
        <div className="px-5 py-4 text-center">
          <p className="font-semibold mb-4">Select Delivery Service Type</p>

          <div className="flex justify-center gap-6">
            {["packky", "restaurant", "doordash"].map((type) => {
              const isDisabled = isDispatched && type !== "packky";

              return (
                <button
                  key={type}
                  onClick={() => {
                    if (!isDisabled) setSelectedService(type);
                  }}
                  disabled={isDisabled}
                  className={`px-6 py-3 rounded-lg border transition ${
                    selectedService === type
                      ? "border-blue-500 text-blue-600 bg-blue-50"
                      : isDisabled
                        ? "text-gray-400 bg-gray-100 cursor-not-allowed opacity-60"
                        : "text-gray-500"
                  }`}
                >
                  {type === "packky"
                    ? "Packky Delivery Service"
                    : type === "restaurant"
                      ? "Restaurant Delivery Service"
                      : "DoorDash Delivery Service"}
                </button>
              );
            })}
          </div>
        </div>

        <hr />

        {/* STEP CONTENT */}
        <div className="px-5 py-5">
          {/* PACKKY */}
          {selectedService === "packky" && (
            <>
              {!isTimerCompleted && !showDriverDetailsAfterFoodReady ? (
                <div
                  className="shadow-sm p-4 rounded-2xl border w-[360px] mx-auto"
                  style={{
                    background: "linear-gradient(135deg, #fff8e8, #ffffff)",
                    border: "1px solid #ffe2a8",
                  }}
                >
                  <h5 className="font-bold mb-2 text-center text-yellow-600">
                    Preparation Timer
                  </h5>

                  <p className="text-gray-500 text-center mb-3">
                    Food is Ready button will be enabled after preparation time
                    is completed and delivery partner acknowledges the order.
                  </p>

                  {/* TIMER */}
                  <div
                    className="mx-auto mb-4 text-center"
                    style={{
                      width: "150px",
                      height: "150px",
                      borderRadius: "50%",
                      background: "#fff",
                      border: "8px solid #ffc107",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "28px",
                        fontWeight: "800",
                        color: "#222",
                      }}
                    >
                      {foodReadyTimer.remainingText}
                    </div>
                    <div style={{ fontSize: "12px", color: "#777" }}>
                      min left
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 text-center mt-3">
                    After the timer is completed, the Food is Ready button will
                    appear once the delivery partner acknowledges the order.
                  </p>
                </div>
              ) : showFoodReady ? (
                <div
                  className="shadow-sm p-4 rounded-2xl border w-[360px] mx-auto"
                  style={{
                    background: "linear-gradient(135deg, #f2fff8, #ffffff)",
                    border: "1px solid #b9f3d2",
                  }}
                >
                  <h5 className="font-bold mb-2 text-center text-green-600">
                    Food Can Be Marked Ready
                  </h5>

                  <p className="text-gray-500 text-center mb-3">
                    Preparation time is completed and delivery partner has
                    acknowledged the order. You can now mark this order as Food
                    is Ready.
                  </p>

                  {/* BUTTON */}
                  <div className="flex justify-center items-center">
                    <button
                      disabled={foodReadyLoading}
                      onClick={async () => {
                        if (foodReadyLoading || foodReadyClicked) return;

                        try {
                          setFoodReadyLoading(true);

                          await notifyDriver({
                            orderId: order.orderid,
                            deliveryPartner: "packky",
                          });

                          if (order?.orderid) {
                            sessionStorage.setItem(
                              `food_ready_clicked_${order.orderid}`,
                              "true",
                            );
                          }

                          setFoodReadyClicked(true);

                          await getCatalogOrder();
                          window.dispatchEvent(new Event("orderUpdated"));
                        } catch (err) {
                          console.error(err);
                        } finally {
                          setFoodReadyLoading(false);
                        }
                      }}
                      className="shadow-sm border-0 p-2 px-5 rounded text-white flex items-center justify-center gap-2 min-w-[150px]"
                      style={{
                        backgroundColor: foodReadyLoading
                          ? "#9ca3af"
                          : "#28a745",
                        cursor: foodReadyLoading ? "not-allowed" : "pointer",
                      }}
                    >
                      {foodReadyLoading ? (
                        <>
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                          Updating...
                        </>
                      ) : (
                        "Food is Ready"
                      )}
                    </button>
                  </div>
                </div>
              ) : showWaitingForPartner ? (
                <div className="bg-gray-100 p-6 rounded-lg text-center">
                  <p className="text-red-500 font-semibold text-lg">
                    Waiting for delivery partner
                  </p>

                  <p className="text-sm text-gray-500 mt-2">
                    Preparation time is completed, but delivery partner has not
                    acknowledged the order yet.
                  </p>
                </div>
              ) : showDriverDetailsAfterFoodReady || isProcessing ? (
                <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-xl text-center shadow-sm">
                  <div className="flex justify-center items-center gap-2 mb-3">
                    <Truck className="text-yellow-600" size={22} />
                    <p className="text-yellow-700 font-semibold text-lg">
                      Delivery is in progress
                    </p>
                  </div>

                  {/* DRIVER DETAILS */}
                  {order.delivery_person_name ||
                  order.delivery_person_number ? (
                    <div className="bg-white border rounded-lg p-4 mt-3 shadow-sm text-left">
                      <p className="text-sm text-gray-500 mb-3 flex items-center gap-2">
                        <User size={16} /> Assigned Driver
                      </p>

                      <h4 className="font-semibold text-gray-800 text-lg flex items-center gap-2">
                        <User size={18} className="text-gray-600" />
                        {order.delivery_person_name || "Driver Assigned"}
                      </h4>

                      {order.delivery_person_number && (
                        <p className="text-gray-600 text-sm mt-2 flex items-center gap-2">
                          <Phone size={16} className="text-green-600" />
                          {order.delivery_person_number}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 mt-2">
                      Driver is being assigned...
                    </p>
                  )}
                </div>
              ) : (
                <div className="bg-gray-100 p-6 rounded-lg text-center">
                  <p className="text-red-500 font-semibold text-lg">
                    Waiting for delivery partner
                  </p>

                  <p className="text-sm text-gray-500 mt-2">
                    Waiting for delivery partner to acknowledge the order.
                  </p>
                </div>
              )}
            </>
          )}

          {/* RESTAURANT */}
          {selectedService === "restaurant" && (
            <div className="text-center text-gray-500">
              Restaurant delivery UI here
            </div>
          )}

          {/* DOORDASH */}
          {selectedService === "doordash" && (
            <div className="text-center text-gray-500">
              DoorDash confirmation UI here
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="flex justify-end gap-2 px-5 py-3 border-t">
          <button onClick={onClose} className="px-4 py-1.5 border rounded">
            Close
          </button>
        </div>
      </div>
    </div>
    </>
  );
};

export default DispatchOrderModel;
